import { CSSProperties, useState } from 'react';
import { useSession, buildSignInUrl } from '../hooks/useSession';

/* Small chip-style auth indicator for Intraop.
 *
 * - Signed out → "Sign in" link that sends the user to the planner's
 *   login page, bringing them back here on success.
 * - Signed in  → email (truncated) + a sign-out button.
 *
 * Designed to be unobtrusive on small screens — sits in a corner,
 * never blocks measurement workflows. */

interface Props {
  /** Optional override for label colour (defaults to muted text). */
  color?: string;
  style?: CSSProperties;
}

export function AuthBadge({ color = 'rgba(245, 245, 247, 0.62)', style }: Props) {
  const { session, loading, signOut } = useSession();
  const [open, setOpen] = useState(false);

  if (loading) {
    return null; // Don't flash a "Sign in" link before we know the state.
  }

  if (!session) {
    return (
      <a
        href={buildSignInUrl()}
        style={{ ...linkStyle, color, ...style }}
        aria-label="Sign in"
      >
        Sign in →
      </a>
    );
  }

  const email = session.user.email ?? 'Signed in';
  // Truncate long emails so the chip stays compact.
  const display = email.length > 24 ? email.slice(0, 22) + '…' : email;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ ...linkStyle, color, cursor: 'pointer' }}
        aria-haspopup="true"
        aria-expanded={open}
        title={email}
      >
        ● {display}
      </button>
      {open ? (
        <button
          type="button"
          onClick={async () => {
            await signOut();
            setOpen(false);
          }}
          style={{ ...linkStyle, color: 'rgba(255, 70, 70, 0.85)', cursor: 'pointer' }}
        >
          Sign out
        </button>
      ) : null}
    </span>
  );
}

const linkStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  font: 'inherit',
  fontSize: 13,
  textDecoration: 'none',
};
